import type {
  KiSyokenFavorite,
  SettingsOptionsKiSyokenResponse,
} from '../domain/kiSyoken/types';

import {
  isKyoyuGroupWithHiddenSyokenJitenFields,
  stripRestrictedKyoyuGroupFieldCodes,
} from './restrictedFieldsByKyoyuGroup';

/**
 * 機種別商圏お気に入りの、制限共有グループ向け補正のみを担当する。
 * 他レポート向けのサニタイズは `sanitizeFavoritesForRestrictedKyoyuGroup.ts` を参照。
 */

type KiSyokenKiFieldOption =
  SettingsOptionsKiSyokenResponse['fields']['ki'][number];

function buildKiFieldByCodeMap(
  kiFieldOptions: KiSyokenKiFieldOption[]
): Map<string, KiSyokenKiFieldOption> {
  return new Map(
    kiFieldOptions.map((fieldOption) => [fieldOption.code, fieldOption])
  );
}

/** お気に入りの列順から、制限対象の自店・商圏接頭辞コードだけを除去する（メイン表・貸玉表）。 */
function stripJitenSyokenPrefixedColumnOrders(
  favorite: KiSyokenFavorite
): KiSyokenFavorite {
  return {
    ...favorite,
    columnsOrder:
      stripRestrictedKyoyuGroupFieldCodes(favorite.columnsOrder) ?? [],
    kasidamaTable: {
      ...favorite.kasidamaTable,
      columnsOrder:
        stripRestrictedKyoyuGroupFieldCodes(
          favorite.kasidamaTable.columnsOrder
        ) ?? [],
    },
  };
}

/**
 * 保存済み `searchParams.fields` が「マスタ上はすべて商圏列」か。
 *
 * Why: 制限ユーザーでは商圏列を UI から隠すため、この状態だと取得後に実質機種名だけになる。
 *      その場合は `fields` を送らず API 既定表示に戻す判定に使う。
 *
 * @returns 判定不能・リセット不要なら `false`（マスタ欠如・fields 空・未知コードのみ・非商圏混在）
 */
function shouldOmitFieldsForSyokenOnlySelection(
  stripped: KiSyokenFavorite,
  kiFieldOptions: KiSyokenKiFieldOption[]
): boolean {
  const fields = stripped.searchParams.fields;
  if (fields == null || fields.length === 0) {
    return false;
  }

  const fieldByCode = buildKiFieldByCodeMap(kiFieldOptions);
  const knownFieldCodes = fields.filter((code) => fieldByCode.has(code));
  if (knownFieldCodes.length === 0) {
    return false;
  }

  return knownFieldCodes.every(
    (code) => fieldByCode.get(code)?.isSyoken === true
  );
}

/** `fields` を外し列順を空にして、初回取得と同様の API 既定表示へ寄せる。 */
function toFavoriteWithImplicitDefaultFields(
  stripped: KiSyokenFavorite
): KiSyokenFavorite {
  const { fields: _discardedFields, ...searchParamsWithoutFields } =
    stripped.searchParams;

  return {
    ...stripped,
    searchParams: searchParamsWithoutFields,
    columnsOrder: [],
  };
}

/**
 * 機種別商圏お気に入りを、特定共有グループ（商圏・自店系を非表示にするユーザー）向けに補正する。
 *
 * Why:
 * - 列順・お気に入りには `jiten*` / `syoken*` 接頭辞のコードや商圏列が残り得るが、当該ユーザーでは画面から商圏列を隠す（`isSyoken` 列を表示しない）。
 * - 非制限ユーザーが「非商圏0・商圏のみ」で保存したお気に入りを、制限ユーザーが適用すると、API は商圏データ列を返すが UI ではそれらがすべて落ち、実質チェック列＋機種名だけになる。
 * - 初回表示は `fields` 未指定で API 既定の表示項目になるため、同じ経路に寄せて通常の表に戻す。
 *
 * What:
 * - 常に（制限ユーザー時）: メイン／貸玉の `columnsOrder` から自店・商圏接頭辞コードを除去（既存仕様）。
 * - `kiFieldOptions` を渡したとき追加で: 保存済み `searchParams.fields` のうちマスタに載るコードがすべて商圏列（`isSyoken === true`）なら、
 *   `fields` をリクエストから外し、`columnsOrder` を空配列にする（`dataKiSyokenSwapColumnsOrder` が「並べ替えなし＝API の列順そのまま」になる）。
 *
 * @param kiFieldOptions `/settings/options/kiSyoken` の `fields.ki`。未取得時は省略可。その場合は上記「fields リセット」は行わない。
 */
export function sanitizeKiSyokenFavoriteForRestrictedKyoyu(
  favorite: KiSyokenFavorite,
  kyoyuGroupCode: string | undefined,
  kiFieldOptions?: KiSyokenKiFieldOption[]
): KiSyokenFavorite {
  if (!isKyoyuGroupWithHiddenSyokenJitenFields(kyoyuGroupCode)) {
    return favorite;
  }

  const stripped = stripJitenSyokenPrefixedColumnOrders(favorite);

  const canEvaluateSyokenOnly =
    kiFieldOptions != null && kiFieldOptions.length > 0;
  if (
    canEvaluateSyokenOnly &&
    shouldOmitFieldsForSyokenOnlySelection(stripped, kiFieldOptions)
  ) {
    return toFavoriteWithImplicitDefaultFields(stripped);
  }

  return stripped;
}
